import { useEffect, useRef, useState } from "react";
import "../asset/css/Footer.css";

const NAV_COLS = [
  { title: "Platform", links: ["How It Works","Submit a Track","Judge Login","Leaderboard","Past Winners"] },
  { title: "Company",  links: ["About Us","Blog","Press","Careers","Partners"] },
  { title: "Support",  links: ["Help Center","Contact Us","Rules & Guidelines","Privacy Policy","Terms of Use"] },
];

const SOCIALS = [
  { icon: "𝕏", label: "Twitter" },
  { icon: "◎", label: "Instagram" },
  { icon: "▶", label: "YouTube" },
  { icon: "♪", label: "TikTok" },
];

const GENRES  = ["Rock","EDM","Vocal","Hip-Hop","Jazz","Classical","World","Pop","Electronic","RnB","Soul","Folk"];
const AWARDS  = ["$50K Prize","Season 4","200+ Judges","48K Artists","120 Countries","8 Genres","Live Now","Submit by Apr 30"];

const STATS = [
  { num: "48", sup: "K+", label: "Artists Competed" },
  { num: "120", sup: "+",  label: "Countries" },
  { num: "$50", sup: "K",  label: "Prize Pool" },
  { num: "200", sup: "+",  label: "Expert Judges" },
];

/* ── Marquee strip ── */
function MarqueeStrip({ items, direction = "fwd", gold = false }) {
  const set = [...items, ...items, ...items, ...items];
  return (
    <div className={`ft3-marquee-wrap ${direction === "bwd" ? "ft3-rev" : ""}`}>
      <div className={`ft3-marquee-track ft3-${direction}`}>
        {set.map((t, i) => (
          <span key={i} className={`ft3-marq-item${gold && i % 3 === 0 ? " ft3-gold" : ""}`}>
            <span className="ft3-mdot" aria-hidden="true" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Canvas Visualizer ── */
function Visualizer() {
  const canvasRef = useRef(null);
  const activeRef = useRef(false);
  const barsRef   = useRef([]);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    function resize() {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
      const count = Math.floor(w / 5);
      barsRef.current = Array.from({ length: count }, (_, i) => ({
        h: Math.random() * 0.3 + 0.05,
        target: Math.random() * 0.5 + 0.05,
        speed: 0.02 + Math.random() * 0.04,
        phase: (i / count) * Math.PI * 2,
      }));
    }

    function loop(t) {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      barsRef.current.forEach((b, i) => {
        let bh;
        if (activeRef.current) {
          b.h += (b.target - b.h) * b.speed;
          if (Math.abs(b.h - b.target) < 0.01) {
            b.target = Math.random() * 0.85 + 0.05;
            b.speed  = 0.03 + Math.random() * 0.06;
          }
          bh = b.h * h;
          const g = Math.round(45 + b.h * 100);
          ctx.fillStyle = `rgba(255,${g},85,${0.5 + b.h * 0.5})`;
          ctx.fillRect(i * 5, h - bh, 3, bh);
          ctx.fillStyle = "rgba(255,200,200,0.7)";
          ctx.fillRect(i * 5, h - bh - 2, 3, 2);
        } else {
          const sine = Math.sin(t * 0.0008 + b.phase) * 0.5 + 0.5;
          bh = (0.06 + sine * 0.16) * h;
          ctx.fillStyle = `rgba(255,45,85,${0.07 + sine * 0.1})`;
          ctx.fillRect(i * 5, h - bh, 3, bh);
        }
      });
      rafRef.current = requestAnimationFrame(loop);
    }

    resize();
    window.addEventListener("resize", resize);
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const toggle = () => {
    activeRef.current = !activeRef.current;
    if (activeRef.current)
      barsRef.current.forEach(b => { b.target = Math.random() * 0.9 + 0.05; });
  };

  return (
    <div className="ft3-viz" onClick={toggle} title="Click to activate visualizer">
      <canvas ref={canvasRef} className="ft3-canvas" />
      <span className="ft3-viz-hint" aria-hidden="true">Click to activate visualizer</span>
    </div>
  );
}

/* ── Footer ── */
const Footer = () => (
  <footer className="ft3">
    <Visualizer />

    {/* CTA band */}
    <div className="ft3-cta">
      <div className="ft3-cta-bigtext" aria-hidden="true">BATTLE</div>
      <div className="ft3-cta-inner">
        <div>
          <span className="ft3-eyebrow">Season 4 · Open Now · $50K Prize</span>
          <h2 className="ft3-cta-h">Ready to take<br />the stage?</h2>
        </div>
        <a href="#signup" className="ft3-cta-btn">
          Enter the Battle <span className="ft3-arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </div>
    <div className="ft3-shimmer-line" aria-hidden="true" />

    {/* marquee 1 */}
    <MarqueeStrip items={GENRES} direction="fwd" />

    {/* body */}
    <div className="ft3-body">
      <div className="ft3-orb ft3-orb1" aria-hidden="true" />
      <div className="ft3-orb ft3-orb2" aria-hidden="true" />

      <div className="ft3-main">
        <div className="ft3-grid">
          {/* brand */}
          <div className="ft3-brand-col">
            <div className="ft3-logo">
              <div className="ft3-logo-icon" aria-hidden="true">♪</div>
              <span className="ft3-logo-text">SoundBattle</span>
            </div>
            <p className="ft3-brand-p">
              The world's most electrifying online music competition.
              Every genre. Every talent. One global stage.
            </p>
            <div className="ft3-socials">
              {SOCIALS.map(({ icon, label }) => (
                <a key={label} href="#" className="ft3-soc" aria-label={label}>{icon}</a>
              ))}
            </div>
            {/* <div className="ft3-live-badge">
              <span className="ft3-live-dot" aria-hidden="true" />
              120+ Countries · Live Now
            </div> */}
          </div>

          {/* nav cols */}
          {NAV_COLS.map((col) => (
            <div key={col.title}>
              <p className="ft3-col-title">{col.title}</p>
              <ul className="ft3-col-list">
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="#" className="ft3-col-link">
                      <span className="ft3-lk-arr" aria-hidden="true">›</span>
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* stats */}
      <div className="ft3-stats">
        {STATS.map((s) => (
          <div key={s.label} className="ft3-stat">
            <span className="ft3-stat-num">
              {s.num}<span className="ft3-stat-sup">{s.sup}</span>
            </span>
            <span className="ft3-stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      {/* marquee 2 */}
      <MarqueeStrip items={AWARDS} direction="bwd" gold />

      {/* bottom bar */}
      <div className="ft3-bottom-wrap">
        <div className="ft3-bottom">
          <p className="ft3-copy">© 2025 SoundBattle Inc. All rights reserved.</p>
          <p className="ft3-tagline">Designed for artists, by artists.</p>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;